import type { PointerEvent } from "react";
import { Button, Icons } from "@/components/ui";
import { selectFrameWindow } from "@/lib/focus-frame-activation";
import type { FrameData } from "@/lib/frame-data";

import { frameDisplayTitle } from "@/lib/frame-display-title";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { frameTypeLabel } from "./frame-type-label";

interface CollapsedFocusFrameTitleProps {
  frameId: string;
  frame: FrameData;
  overflowCount?: number;
}

export function CollapsedFocusFrameTitle({
  frameId,
  frame,
  overflowCount,
}: CollapsedFocusFrameTitleProps) {
  const t = useT(framesSharedMessages);
  const frameTitle = frameDisplayTitle(frame);
  const title = overflowCount
    ? t("moreFrames", { count: overflowCount })
    : frameTitle;
  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    <Button
      type="button"
      variant="secondary"
      className="nodrag nowheel absolute inset-0 z-[90] h-auto w-full justify-start gap-2 overflow-hidden rounded-frame border-glass-stroke bg-surface px-3 text-left text-appkit-caption font-semibold text-primary shadow-frame hover:bg-glass-fill-heavy"
      aria-label={
        overflowCount ? t("openOneOf", { title }) : t("openFrame", { title })
      }
      aria-expanded={false}
      data-testid={`conversation-stack-title-${frameId}`}
      onClick={(event) => {
        event.stopPropagation();
        selectFrameWindow(frameId);
      }}
      onPointerDown={handlePointerDown}
    >
      <Icons.Frame className="h-3 w-3 shrink-0 text-muted" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate" title={title}>
        {title}
      </span>
      {!overflowCount && (
        <span className="max-w-[40%] shrink-0 truncate text-appkit-mini font-medium text-muted">
          {frameTypeLabel(frame.frameType)}
        </span>
      )}
    </Button>
  );
}
